import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileText, Wifi } from "lucide-react";

export default function TermsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="border-b border-white/[0.06] bg-[#0A0A0A]/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#34C759] to-[#28a745] rounded-xl flex items-center justify-center">
              <Wifi className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">AVX RF PRO</h1>
              <p className="text-[9px] text-[#34C759] font-medium tracking-widest">TÉRMINOS DE SERVICIO</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            onClick={() => navigate("/")}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-[#34C759]/20 rounded-xl flex items-center justify-center">
            <FileText className="w-6 h-6 text-[#34C759]" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Términos y Condiciones</h1>
            <p className="text-gray-400">Última actualización: Marzo 2026</p>
          </div>
        </div>

        <div className="prose prose-invert max-w-none space-y-8">
          <section className="bg-[#111111] border border-white/[0.08] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#34C759] mb-4">1. Aceptación de los Términos</h2>
            <p className="text-gray-300 leading-relaxed">
              Al acceder y utilizar AVX RF PRO ("la Plataforma"), usted acepta estar sujeto a estos Términos y Condiciones. 
              Si no está de acuerdo con alguna parte de estos términos, no podrá acceder al servicio.
            </p>
          </section>

          <section className="bg-[#111111] border border-white/[0.08] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#34C759] mb-4">2. Descripción del Servicio</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              AVX RF PRO es una plataforma profesional para la gestión y coordinación de frecuencias RF en sistemas 
              de monitoreo In-Ear (IEM). El servicio incluye:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2">
              <li>Gestión de sistemas IEM multi-marca</li>
              <li>Asignación inteligente de frecuencias</li>
              <li>Análisis de espectro RF</li>
              <li>Calculadora de intermodulación</li>
              <li>Generación de reportes</li>
              <li>Integración con hardware USB (tinySA, Relacart)</li>
            </ul>
          </section>

          <section className="bg-[#111111] border border-white/[0.08] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#34C759] mb-4">3. Cuentas de Usuario</h2>
            <p className="text-gray-300 leading-relaxed">
              Para utilizar ciertas funciones de la Plataforma, debe crear una cuenta. Usted es responsable de:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 mt-4">
              <li>Mantener la confidencialidad de su contraseña</li>
              <li>Todas las actividades que ocurran bajo su cuenta</li>
              <li>Notificarnos inmediatamente de cualquier uso no autorizado</li>
              <li>Proporcionar información precisa y actualizada</li>
            </ul>
          </section>

          <section className="bg-[#111111] border border-white/[0.08] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#34C759] mb-4">4. Planes de Suscripción</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Ofrecemos diferentes planes de suscripción:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2">
              <li><strong className="text-white">Free:</strong> Funcionalidad básica con límites</li>
              <li><strong className="text-white">Pro:</strong> Funciones avanzadas para profesionales</li>
              <li><strong className="text-white">Enterprise:</strong> Sin límites, múltiples usuarios, soporte prioritario</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              Los pagos se procesan a través de Flow.cl. Las suscripciones se renuevan automáticamente 
              a menos que se cancelen antes del período de facturación.
            </p>
          </section>

          <section className="bg-[#111111] border border-white/[0.08] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#34C759] mb-4">5. Uso Aceptable</h2>
            <p className="text-gray-300 leading-relaxed">
              Usted se compromete a no utilizar la Plataforma para:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 mt-4">
              <li>Violar cualquier ley o regulación aplicable</li>
              <li>Interferir con el funcionamiento de la Plataforma</li>
              <li>Intentar acceder a cuentas de otros usuarios</li>
              <li>Transmitir malware o código malicioso</li>
              <li>Realizar ingeniería inversa del software</li>
            </ul>
          </section>

          <section className="bg-[#111111] border border-white/[0.08] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#34C759] mb-4">6. Propiedad Intelectual</h2>
            <p className="text-gray-300 leading-relaxed">
              Todo el contenido, características y funcionalidad de AVX RF PRO son propiedad de Anleon Chile 
              y están protegidos por leyes de propiedad intelectual. No se otorga ninguna licencia o derecho 
              de propiedad sobre el software más allá del uso permitido.
            </p>
          </section>

          <section className="bg-[#111111] border border-white/[0.08] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#34C759] mb-4">7. Limitación de Responsabilidad</h2>
            <p className="text-gray-300 leading-relaxed">
              AVX RF PRO se proporciona "tal cual" sin garantías de ningún tipo. No garantizamos que el servicio 
              sea ininterrumpido, seguro o libre de errores. En ningún caso seremos responsables por daños 
              indirectos, incidentales o consecuentes.
            </p>
          </section>

          <section className="bg-[#111111] border border-white/[0.08] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#34C759] mb-4">8. Modificaciones</h2>
            <p className="text-gray-300 leading-relaxed">
              Nos reservamos el derecho de modificar estos términos en cualquier momento. Los cambios entrarán 
              en vigor inmediatamente después de su publicación. El uso continuado de la Plataforma constituye 
              la aceptación de los términos modificados.
            </p>
          </section>

          <section className="bg-[#111111] border border-white/[0.08] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#34C759] mb-4">9. Contacto</h2>
            <p className="text-gray-300 leading-relaxed">
              Para preguntas sobre estos Términos, contáctenos en:
            </p>
            <div className="mt-4 p-4 bg-[#0A0A0A] rounded-lg">
              <p className="text-white font-medium">Anleon Chile</p>
              <p className="text-gray-400">Email: contacto@avxrfpro.com</p>
              <p className="text-gray-400">Santiago, Chile</p>
            </div>
          </section>
        </div>

        <div className="mt-12 text-center">
          <Button 
            onClick={() => navigate("/")}
            className="bg-[#34C759] hover:bg-[#2db54d] text-black font-semibold px-8"
          >
            Volver al Inicio
          </Button>
        </div>
      </main>
    </div>
  );
}
